import { useEffect, useState } from 'react';
import { type Role, api } from '../account/api';
import { locale, t } from '../i18n/i18n';
import { store, useApp } from '../state/store';
import { ROLE_NAMES } from './AccountDialog';
import { IconClose, IconTrash } from './icons';

interface AdminUser {
  id: number;
  email: string;
  role: Role;
  status: 'pending' | 'active' | 'blocked';
  createdAt: string;
  lastLoginAt: string | null;
  projectCount: number;
}

interface EmailSettings {
  senderEmail: string;
  senderName: string;
  apiKeySet: boolean;
  apiKeyHint: string | null;
  source: 'database' | 'environment' | 'none';
}

const STATUS_NAMES = { pending: 'Not confirmed', active: 'Active', blocked: 'Blocked' };

/** User administration (admins) and email settings (superadmin). */
export function AdminDialog() {
  const me = useApp((s) => s.account.user);
  const [tab, setTab] = useState<'users' | 'email'>('users');
  const close = () => store.set({ panel: null });
  if (!me || (me.role !== 'admin' && me.role !== 'superadmin')) return null;
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="admin-title" onKeyDown={(e) => e.key === 'Escape' && close()}>
      <div className="modal">
        <div className="modal-head">
          <h2 id="admin-title">{t('Administration')}</h2>
          <button className="btn icon ghost" aria-label={t('Close dialog')} onClick={close}>
            <IconClose />
          </button>
        </div>
        {me.role === 'superadmin' && (
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'users'} onClick={() => setTab('users')}>
              {t('Users')}
            </button>
            <button role="tab" aria-selected={tab === 'email'} onClick={() => setTab('email')}>
              {t('Email')}
            </button>
          </div>
        )}
        <div className="modal-body">{tab === 'users' ? <Users meId={me.id} superadmin={me.role === 'superadmin'} /> : <EmailSettingsForm adminEmail={me.email} />}</div>
      </div>
    </div>
  );
}

function Users({ meId, superadmin }: { meId: number; superadmin: boolean }) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const load = async (search: string, offset = 0) => {
    try {
      const r = await api<{ users: AdminUser[]; total: number }>('GET', `/api/admin/users?q=${encodeURIComponent(search)}&offset=${offset}`);
      setUsers((cur) => (offset ? [...(cur ?? []), ...r.users] : r.users));
      setTotal(r.total);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => void load(q), 250);
    return () => clearTimeout(timer);
  }, [q]);

  const act = async (f: () => Promise<unknown>) => {
    try {
      await f();
      setConfirmDelete(null);
      await load(q);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  // admins manage ordinary users; the superadmin also manages admins, and nobody manages the superadmin
  const mayManage = (u: AdminUser) => u.id !== meId && u.role !== 'superadmin' && (superadmin || u.role === 'user');

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <input className="input" type="search" placeholder={t('Search by email')} aria-label={t('Search by email')} value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320 }} />
        <span className="hint" style={{ margin: 0 }}>
          {total === 1 ? t('1 user') : t('{n} users', { n: total })}
        </span>
      </div>
      {error && (
        <p className="issue error" role="alert">
          {error}
        </p>
      )}
      {users === null ? (
        <p className="hint">{t('Loading …')}</p>
      ) : (
        <ul className="user-list" aria-label={t('Users')}>
          {users.map((u) => (
            <li key={u.id} className="user-row">
              <div className="user-main">
                <b>{u.email}</b>
                <span className={'badge' + (u.role === 'user' ? '' : ' ok')}>{t(ROLE_NAMES[u.role])}</span>
                <span className={'badge' + (u.status === 'blocked' ? ' err' : u.status === 'pending' ? ' warn' : '')}>{t(STATUS_NAMES[u.status])}</span>
              </div>
              <small className="hint" style={{ margin: 0 }}>
                {t('Created {date}', { date: new Date(u.createdAt).toLocaleDateString(locale()) })} ·{' '}
                {u.lastLoginAt ? t('last logged in {date}', { date: new Date(u.lastLoginAt).toLocaleString(locale()) }) : t('never logged in')} · {t('{n} cloud projects', { n: u.projectCount })}
              </small>
              {mayManage(u) && (
                <div className="row wrap">
                  {u.status !== 'pending' && (
                    <button className="btn small" onClick={() => act(() => api('PATCH', `/api/admin/users/${u.id}`, { status: u.status === 'blocked' ? 'active' : 'blocked' }))}>
                      {u.status === 'blocked' ? t('Unblock') : t('Block')}
                    </button>
                  )}
                  {superadmin && (
                    <button className="btn small" onClick={() => act(() => api('PATCH', `/api/admin/users/${u.id}`, { role: u.role === 'admin' ? 'user' : 'admin' }))}>
                      {u.role === 'admin' ? t('Remove admin role') : t('Make admin')}
                    </button>
                  )}
                  {confirmDelete === u.id ? (
                    <button className="btn small danger" onClick={() => act(() => api('DELETE', `/api/admin/users/${u.id}`))}>
                      {t('Delete {email} and their cloud projects', { email: u.email })}
                    </button>
                  ) : (
                    <button className="btn small icon danger" aria-label={t('Delete user')} title={t('Delete user')} onClick={() => setConfirmDelete(u.id)}>
                      <IconTrash size={14} />
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {users && users.length < total && (
        <button className="btn" onClick={() => void load(q, users.length)}>
          {t('Show more')}
        </button>
      )}
    </>
  );
}

function EmailSettingsForm({ adminEmail }: { adminEmail: string }) {
  const [s, setS] = useState<EmailSettings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [removeKey, setRemoveKey] = useState(false);
  const [message, setMessage] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api<EmailSettings>('GET', '/api/admin/settings/email').then(setS, (e) => setMessage({ kind: 'error', text: e.message }));
  useEffect(() => {
    void load();
  }, []);
  const run = async (f: () => Promise<string>) => {
    setBusy(true);
    setMessage(null);
    try {
      setMessage({ kind: 'info', text: await f() });
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  if (!s) return message ? <p className="issue error">{message.text}</p> : <p className="hint">{t('Loading …')}</p>;

  const SOURCE = { database: t('The API key is stored here (encrypted).'), environment: t('The API key comes from the server environment (BREVO_API_KEY).'), none: t('No API key: emails cannot be sent.') };
  return (
    <form
      style={{ maxWidth: 480 }}
      onSubmit={(e) => {
        e.preventDefault();
        void run(async () => {
          await api('PUT', '/api/admin/settings/email', { senderEmail: s.senderEmail, senderName: s.senderName, ...(removeKey ? { apiKey: '' } : apiKey.trim() ? { apiKey: apiKey.trim() } : {}) });
          setApiKey('');
          setRemoveKey(false);
          await load();
          return t('The email settings are saved.');
        });
      }}
    >
      <p className="hint" style={{ marginTop: 0 }}>{t('Emails (codes for confirming addresses and resetting passwords) are sent with Brevo.')}</p>
      <label className="field" style={{ marginBottom: 10 }}>
        <span>{t('Brevo API key')}</span>
        <input
          className="input"
          type="password"
          autoComplete="off"
          placeholder={s.apiKeySet ? t('Stored ({hint}) – leave empty to keep it', { hint: s.apiKeyHint ?? '' }) : 'xkeysib-…'}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
      </label>
      <p className="hint">{SOURCE[s.source]}</p>
      {s.source === 'database' && (
        <label className="check">
          <input type="checkbox" checked={removeKey} onChange={(e) => setRemoveKey(e.target.checked)} /> {t('Remove the stored key')}
        </label>
      )}
      <div className="grid2" style={{ marginTop: 10 }}>
        <label className="field">
          <span>{t('Sender address')}</span>
          <input className="input" type="email" required value={s.senderEmail} onChange={(e) => setS({ ...s, senderEmail: e.target.value })} />
        </label>
        <label className="field">
          <span>{t('Sender name')}</span>
          <input className="input" value={s.senderName} onChange={(e) => setS({ ...s, senderName: e.target.value })} />
        </label>
      </div>
      <p className="hint">{t('The sender address must be a verified sender or domain in Brevo.')}</p>
      {message && (
        <p className={'issue ' + message.kind} role={message.kind === 'error' ? 'alert' : undefined}>
          {message.text}
        </p>
      )}
      <div className="row wrap" style={{ marginTop: 10 }}>
        <button className="btn primary" type="submit" disabled={busy}>
          {t('Save')}
        </button>
        <button
          className="btn"
          type="button"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await api('POST', '/api/admin/settings/email/test');
              return t('A test email is sent to {email}.', { email: adminEmail });
            })
          }
        >
          {t('Send a test email to {email}', { email: adminEmail })}
        </button>
      </div>
    </form>
  );
}
